import { useState } from "react"
import { Link } from "react-router-dom"
import { useDispatch } from "react-redux"
import { Trans, useTranslation } from "react-i18next"

import ModalService from "src/modals"
import { loginWithGoogle, registerAndSignIn } from "src/store/auth"

import Modal from "src/components/modals/Modal"
import GoogleSignInButton from "src/components/GoogleSignInButton"
import AudioSettings from "src/components/AudioSettings"
import PlayAsGuestButton from "src/components/PlayAsGuestButton"
import { apiErrorLines, fieldError } from "src/utils/apiErrors"
import { USERNAME_MAX_LENGTH, usernameProblem } from "src/utils/username"

const RegisterModal = ({ onLogin }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [errors, setErrors] = useState([])
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    email: "",
  })
  // A weak password (gamekit 0.11.0 runs Django's validators at registration) is said under the
  // password input, where the player is about to retype it, and not again in the list at the top.
  const passwordProblem = fieldError(errors, "password")
  // Nothing to say about an empty field they have not filled in yet.
  const nameProblem = formData.username ? usernameProblem(formData.username) : null
  const handleSubmit = (e) => {
    e.preventDefault()

    dispatch(registerAndSignIn(formData))
      .unwrap()
      .then(() => ModalService.hideAll())
      .catch(setErrors)
  }
  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })
    setErrors([])
  }

  // The same call the login modal makes: a first Google sign-in *is* the registration, so there is
  // no second step and no form to fill in afterwards. The username comes from the Google profile
  // and the server makes it unique.
  const handleGoogleCredential = (credential) => {
    setErrors([])
    dispatch(loginWithGoogle(credential))
      .unwrap()
      .then(() => ModalService.hideAll())
      .catch(setErrors)
  }

  return (
    <Modal>
      <h1>{t("auth.registerTitle")}</h1>
      {apiErrorLines(errors, { except: ["password"] }).map(({ key, text }) => (
        <p key={key} className="errored-text">
          {text}
        </p>
      ))}
      <form onSubmit={handleSubmit}>
        <label htmlFor="username">{t("auth.usernameLabel")}</label>
        <input
          type="text"
          id="username"
          name="username"
          className="form-input"
          placeholder={t("auth.usernamePlaceholder")}
          value={formData.username}
          onChange={handleInputChange}
          maxLength={USERNAME_MAX_LENGTH}
          required
        />
        {/* The same courtesy the profile editor gives: the server holds the rule, this says so
            while they are typing rather than after a round trip. Until now this form said nothing
            and the server checked nothing either, so a name the player could never rename *to*
            was the one they registered with. */}
        {nameProblem && <p className="errored-text">{t(nameProblem.key, nameProblem.values)}</p>}

        <label htmlFor="email">{t("auth.emailLabel")}</label>
        <input
          type="email"
          id="email"
          name="email"
          className="form-input"
          placeholder={t("auth.emailPlaceholder")}
          value={formData.email}
          onChange={handleInputChange}
          required
        />

        <label htmlFor="password">{t("auth.passwordLabel")}</label>
        <input
          type="password"
          id="password"
          name="password"
          className="form-input"
          placeholder={t("auth.passwordPlaceholder")}
          value={formData.password}
          onChange={handleInputChange}
          aria-invalid={Boolean(passwordProblem)}
          required
        />
        {passwordProblem && <p className="errored-text">{passwordProblem}</p>}

        {/* Google asks for both pages before its sign-in leaves testing; signing up is accepting them. */}
        <p className="register-legal">
          <Trans
            i18nKey="legal.acceptance"
            components={{
              terms: <Link to="/terms" onClick={() => ModalService.hideAll()} />,
              privacy: <Link to="/privacy" onClick={() => ModalService.hideAll()} />,
            }}
          />
        </p>

        <div className="modal-actions">
          <button type="submit">{t("auth.registerButton")}</button>
          <button type="button" className="quiet" onClick={onLogin}>
            {t("auth.alreadyHaveAccount")}
          </button>
        </div>
      </form>

      {/* "Continue", not "sign up" (owner, 2026-09-20: "Registrarse con Google ⇒ Seguir con Google"):
          the account exists after the first press, and Google draws the words itself, so
          `continue_with` is the nearest of the four it offers. */}
      <GoogleSignInButton onCredential={handleGoogleCredential} text="continue_with" />

      {/* No email, no password: a funny name and straight in (owner, 2026-10-02). */}
      <PlayAsGuestButton />

      {/* The menu music is already playing while this form is open, and Settings lives
          behind the account being created (Q18) — so the volume comes to the form. */}
      <AudioSettings />
    </Modal>
  )
}

export default RegisterModal
